import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface RealTimeState {
  isConnected: boolean;
  activeUsers: number;
  liveVotes: Record<string, number[]>; // questionId -> vote counts
  notifications: Array<{
    id: string;
    message: string;
    type: 'vote' | 'user_joined' | 'user_left' | 'presentation_started';
    timestamp: string;
  }>;
  votingActivity: Array<{
    id: string;
    userId: string;
    userName: string;
    questionId: string;
    optionIndex: number;
    timestamp: string;
  }>;
}

const initialState: RealTimeState = {
  isConnected: true,
  activeUsers: 47,
  liveVotes: {},
  notifications: [],
  votingActivity: [],
};

export const realTimeSlice = createSlice({
  name: 'realTime',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    updateActiveUsers: (state, action: PayloadAction<number>) => {
      state.activeUsers = action.payload;
    },
    updateLiveVotes: (state, action: PayloadAction<{ questionId: string; votes: number[] }>) => {
      state.liveVotes[action.payload.questionId] = action.payload.votes;
    },
    addNotification: (state, action: PayloadAction<{
      message: string;
      type: 'vote' | 'user_joined' | 'user_left' | 'presentation_started';
    }>) => {
      const notification = {
        id: Date.now().toString(),
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      state.notifications.unshift(notification);
      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },
    addVotingActivity: (state, action: PayloadAction<{
      userId: string;
      userName: string;
      questionId: string;
      optionIndex: number;
    }>) => {
      const activity = {
        id: Date.now().toString(),
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      state.votingActivity.unshift(activity);
      // Keep only last 100 activities
      if (state.votingActivity.length > 100) {
        state.votingActivity = state.votingActivity.slice(0, 100);
      }
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    simulateVoteUpdate: (state, action: PayloadAction<string>) => {
      const questionId = action.payload;
      if (state.liveVotes[questionId]) {
        // Randomly increment one of the options
        const optionIndex = Math.floor(Math.random() * state.liveVotes[questionId].length);
        state.liveVotes[questionId][optionIndex] += 1;
      }
    },
  },
});

export const {
  setConnectionStatus,
  updateActiveUsers,
  updateLiveVotes,
  addNotification,
  addVotingActivity,
  clearNotifications,
  simulateVoteUpdate,
} = realTimeSlice.actions;

export default realTimeSlice.reducer;
